import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Clock, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { newsAPI, newsletterAPI } from '../services/api';

const NewsArticle = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [article, setArticle] = useState<any>(null);
  const [relatedArticles, setRelatedArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  useEffect(() => {
    fetchArticle();
  }, [id, language]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const articleData = await newsAPI.getById(parseInt(id || '0'), language);
      setArticle(articleData);
      
      // Fetch related articles
      const allArticles = await newsAPI.getAll({ limit: 3, lang: language });
      if (allArticles.records) {
        const filtered = allArticles.records
          .filter((a: any) => a.id !== parseInt(id || '0'))
          .slice(0, 3);
        setRelatedArticles(filtered);
      }
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    try {
      setNewsletterLoading(true);
      await newsletterAPI.subscribe(newsletterEmail, language);
      setNewsletterMessage('Inscription réussie !');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterMessage('Erreur lors de l\'inscription');
    } finally {
      setNewsletterLoading(false);
      setTimeout(() => setNewsletterMessage(''), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const shareUrl = window.location.href;
  const shareTitle = article?.title || '';

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('news.article.notFound')}</h1>
          <Link to="/news" className="text-blue-600 hover:text-blue-800">
            {t('news.article.backToNews')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Article Header */}
      <section className="py-12 bg-gradient-to-br from-blue-900 to-teal-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/news" 
            className="inline-flex items-center space-x-2 text-blue-200 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t('news.article.backToNews')}</span>
          </Link>
          
          <div className="mb-6">
            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              {article.category}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {article.title}
          </h1>
          
          <div className="flex flex-wrap items-center space-x-6 text-blue-200">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(article.created_at)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4" />
              <span>{article.author}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Featured Image */}
              {article.image_url && (
                <div className="mb-8">
                  <img 
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              )}

              {/* Article Excerpt */}
              {article.excerpt && (
                <div className="bg-blue-50 p-6 rounded-2xl mb-8 border-l-4 border-blue-500">
                  <p className="text-lg text-blue-800 font-medium leading-relaxed">
                    {article.excerpt}
                  </p>
                </div>
              )}

              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {article.content}
                </div>
              </div>

              {/* Tags */}
              {article.tags && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('news.article.tags')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.split(',').map((tag: string, index: number) => (
                      <span 
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center"
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('news.article.share')}</h3>
                <div className="flex space-x-4">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-sky-500 hover:bg-sky-600 text-white p-3 rounded-lg transition-colors"
                  >
                    <Twitter className="h-5 w-5" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-700 hover:bg-blue-800 text-white p-3 rounded-lg transition-colors"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                  <button
                    onClick={() => navigator.share && navigator.share({ title: shareTitle, url: shareUrl })}
                    className="bg-gray-600 hover:bg-gray-700 text-white p-3 rounded-lg transition-colors"
                  >
                    <Share2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar - Articles Similaires avec Design Amélioré */}
            <div className="lg:col-span-1">
              {relatedArticles.length > 0 && (
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl shadow-lg p-6 sticky top-24 border border-blue-100">
                  <div className="flex items-center mb-6">
                    <div className="bg-blue-500 p-2 rounded-lg mr-3">
                      <Eye className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{t('news.article.relatedArticles')}</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {relatedArticles.map((related, index) => (
                      <Link
                        key={related.id}
                        to={`/news/${related.id}`}
                        className="block group"
                      >
                        <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                          <div className="relative h-32 mb-4 overflow-hidden rounded-lg">
                            <img 
                              src={related.image_url}
                              alt={related.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                            <div className="absolute top-2 left-2">
                              <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                {index + 1}
                              </span>
                            </div>
                          </div>
                          
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-3 line-clamp-2 leading-tight">
                            {related.title}
                          </h4>
                          
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              <span>{formatDate(related.created_at)}</span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              <span>{related.read_time}min</span>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <span className="text-blue-600 text-sm font-medium group-hover:text-blue-800 transition-colors">
                              Lire l'article →
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <Link 
                      to="/news"
                      className="block text-center bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
                    >
                      Voir toutes les actualités
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('news.article.newsletter.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('news.article.newsletter.subtitle')}
          </p>
          
          {newsletterMessage && (
            <div className="mb-6 p-4 bg-white/20 rounded-lg text-white">
              {newsletterMessage}
            </div>
          )}
          
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={t('news.newsletter.placeholder')}
              className="flex-1 px-6 py-4 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-white/30"
              required
            />
            <button 
              type="submit"
              disabled={newsletterLoading}
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
            >
              {newsletterLoading ? 'Inscription...' : t('news.newsletter.subscribe')}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default NewsArticle;